import { useState } from "react";
import * as actions from "../../../../Redux/Actions/index";
import { useDispatch } from "react-redux";
import {
  showErrorToastMessage,
  showSuccessToastMessage,
} from "../../../../component/Toasts/Toasts";
import { Strings } from "../../../../constant/Strings";
import { ONSURITY_PLAN_COMBOS } from "../../../../constant/OnsurityPlans";
import { BsFillPersonCheckFill, BsPeopleFill, BsFillHeartFill, BsPlusLg, BsTrash } from "react-icons/bs";
import "./EditInsuranceDetails.scss";

const RELATIONS = [
  "Spouse",
  "Mother",
  "Father",
  "Father_in_law",
  "Mother_in_law",
  "Child",
];

// Onsurity's max team size per employee is employee + nominee + 5 dependents.
const MAX_DEPENDENTS = 5;

const today = new Date();
const MIN_DOB = "1900-01-01";
const MAX_DOB = today.toISOString().substring(0, 10);
const isValidDob = (value: string) => !value || (value >= MIN_DOB && value <= MAX_DOB);

// Relations that represent one specific person — can only be used once across the whole
// family group (nominee + dependents). Child is deliberately excluded since multiple kids are
// normal.
const UNIQUE_RELATIONS = new Set(["Spouse", "Mother", "Father", "Father_in_law", "Mother_in_law"]);

// An employee's own parents (Father/Mother) and their in-law counterparts (Father_in_law/
// Mother_in_law) are two mutually exclusive groups across the whole family group — selecting
// anyone from one group blocks every relation in the other group, everywhere else, per
// Onsurity's plan slot rules.
const PARENT_GROUP = ["Father", "Mother"];
const PARENT_IN_LAW_GROUP = ["Father_in_law", "Mother_in_law"];

const EditInsuranceDetails = (props: any) => {
  const { onsurity, employeeId, handleEditInsurance, refreshData, isHr } = props;
  const { editSuccess, cancel, submit } = Strings;
  const [btnDisable, setBtnDisable] = useState(true);

  const [dependents, setDependents] = useState<any[]>(
    (onsurity?.dependents || []).map((d: any) => ({
      name: d.name || "",
      gender: d.gender || "",
      dob: d.dob ? new Date(d.dob).toISOString().substring(0, 10) : "",
      relation: d.relation || "",
    }))
  );

  // Onsurity requires the nominee to be one of the family members already on the plan — not a
  // separate person — so the nominee is a reference to a dependent row, not its own free-text
  // entry. This also rules out the "same person, different details" mismatch Onsurity's API
  // rejects when a nominee and a dependent describe the same relation inconsistently.
  const [nomineeIndex, setNomineeIndex] = useState<number>(() => {
    if (!onsurity?.nominee?.name) return -1;
    return (onsurity?.dependents || []).findIndex(
      (d: any) => d.name === onsurity.nominee.name && d.relation === onsurity.nominee.relation
    );
  });
  const nominee = nomineeIndex >= 0 ? dependents[nomineeIndex] : undefined;

  const [healthPlan, setHealthPlan] = useState({
    planName: onsurity?.healthPlan?.planName || "",
    policyType: onsurity?.healthPlan?.policyType || "",
  });

  const [opdPlan, setOpdPlan] = useState({
    planName: onsurity?.opdPlan?.planName || "",
    policyType: onsurity?.opdPlan?.policyType || "",
  });

  const [lifePlan, setLifePlan] = useState({
    planName: onsurity?.lifePlan?.planName || "",
    policyType: onsurity?.lifePlan?.policyType || "",
  });

  const dispatch = useDispatch();

  const comboKey = healthPlan.planName ? `${healthPlan.planName}|${opdPlan.planName}` : "";
  const knownCombo = ONSURITY_PLAN_COMBOS.some((c) => c.key === comboKey);

  const handleComboSelect = (key: string) => {
    const combo = ONSURITY_PLAN_COMBOS.find((c) => c.key === key);
    setHealthPlan(combo ? { planName: combo.main.planName, policyType: combo.main.policyType } : { planName: "", policyType: "" });
    setOpdPlan(combo?.opd ? { planName: combo.opd.planName, policyType: combo.opd.policyType } : { planName: "", policyType: "" });
  };

  const addDependentRow = () => {
    if (dependents.length >= MAX_DEPENDENTS) return;
    setDependents([...dependents, { name: "", gender: "", dob: "", relation: "" }]);
  };

  // Relations already used elsewhere among the dependents, excluding whichever row is asking
  // (by index) — used to disable the conflicting Father/Father_in_law or Mother/Mother_in_law
  // option in every other dropdown. The nominee is just one of these rows, not a separate source.
  const getRelationsUsedElsewhere = (source: number) => {
    const used = new Set<string>();
    dependents.forEach((d, i) => {
      if (i !== source && d.relation) used.add(d.relation);
    });
    return used;
  };

  const isRelationDisabled = (relation: string, usedElsewhere: Set<string>) => {
    // No two Fathers, no two Spouses, etc.
    if (UNIQUE_RELATIONS.has(relation) && usedElsewhere.has(relation)) return true;
    // Picking any parent blocks every in-law relation, and vice versa.
    if (PARENT_GROUP.includes(relation) && PARENT_IN_LAW_GROUP.some((r) => usedElsewhere.has(r))) return true;
    if (PARENT_IN_LAW_GROUP.includes(relation) && PARENT_GROUP.some((r) => usedElsewhere.has(r))) return true;
    return false;
  };

  const removeDependentRow = (index: number) => {
    setDependents(dependents.filter((_, i) => i !== index));
    if (nomineeIndex === index) setNomineeIndex(-1);
    else if (nomineeIndex > index) setNomineeIndex(nomineeIndex - 1);
  };

  const updateDependentField = (index: number, field: string, value: string) => {
    const updated = [...dependents];
    updated[index] = { ...updated[index], [field]: value };
    setDependents(updated);
  };

  const handleSubmit = () => {
    if (nominee && !isValidDob(nominee.dob)) {
      showErrorToastMessage("Nominee's date of birth is invalid.");
      return;
    }
    const invalidDependent = dependents.find((d) => d.name && !isValidDob(d.dob));
    if (invalidDependent) {
      showErrorToastMessage(`${invalidDependent.name}'s date of birth is invalid.`);
      return;
    }

    setBtnDisable(true);
    const payload: any = {
      nominee: nominee?.name
        ? { name: nominee.name, dob: nominee.dob, relation: nominee.relation }
        : undefined,
      dependents: dependents.filter((d) => d.name),
    };
    // Plan selection is HR-only — a non-HR viewer only ever edits their own nominee/dependents,
    // so the plan fields are left out of the payload entirely rather than trusting client state,
    // even though the plan section isn't rendered for them either.
    if (isHr) {
      if (healthPlan.planName) payload.healthPlan = healthPlan;
      if (opdPlan.planName) payload.opdPlan = opdPlan;
      if (lifePlan.planName) payload.lifePlan = lifePlan;
    }

    dispatch(
      actions.updateEmployeeInsuranceAction(employeeId, payload, (res: any) => {
        if (res?.error?.error_description) {
          showErrorToastMessage(res.error.error_description);
        } else {
          showSuccessToastMessage(editSuccess);
          handleEditInsurance();
          refreshData?.();
        }
      })
    );
  };

  return (
    <div className="edit-insurance-form">
      <div className="insurance-section">
        <div className="insurance-section-header">
          <h6>
            <BsPeopleFill /> Dependents
          </h6>
          <span className="insurance-section-hint">
            {dependents.length} added
          </span>
        </div>

        {dependents.length === 0 && (
          <p className="no-dependents-hint">No dependents added yet.</p>
        )}

        {dependents.map((dep, index) => (
          <div className="dependent-card" key={index}>
            <div>
              <label className="form-label">Name</label>
              <input
                type="text"
                className="form-control"
                placeholder="Name"
                value={dep.name}
                onChange={(e) => updateDependentField(index, "name", e.target.value)}
              />
            </div>
            <div>
              <label className="form-label">Gender</label>
              <select
                className="form-control"
                value={dep.gender}
                onChange={(e) => updateDependentField(index, "gender", e.target.value)}
              >
                <option value="">Select</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
            </div>
            <div>
              <label className="form-label">Date of Birth</label>
              <input
                type="date"
                className="form-control"
                min={MIN_DOB}
                max={MAX_DOB}
                value={dep.dob}
                onChange={(e) => updateDependentField(index, "dob", e.target.value)}
              />
            </div>
            <div>
              <label className="form-label">Relation</label>
              <select
                className="form-control"
                value={dep.relation}
                onChange={(e) => updateDependentField(index, "relation", e.target.value)}
              >
                <option value="">Select</option>
                {RELATIONS.map((r) => (
                  <option key={r} value={r} disabled={isRelationDisabled(r, getRelationsUsedElsewhere(index))}>
                    {r}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              className="remove-dependent-btn"
              title="Remove dependent"
              onClick={() => removeDependentRow(index)}
            >
              <BsTrash />
            </button>
          </div>
        ))}

        {dependents.length < MAX_DEPENDENTS ? (
          <button type="button" className="add-dependent-btn" onClick={addDependentRow}>
            <BsPlusLg /> {Strings.onsurity_add_dependent}
          </button>
        ) : (
          <p className="no-dependents-hint">Maximum of {MAX_DEPENDENTS} dependents reached.</p>
        )}
      </div>

      <div className="insurance-section">
        <div className="insurance-section-header">
          <h6>
            <BsFillPersonCheckFill /> Nominee
          </h6>
        </div>
        <div className="row g-3">
          <div className="col-md-6">
            <label className="form-label">Select from Dependents</label>
            <select
              className="form-control"
              value={nomineeIndex}
              onChange={(e) => setNomineeIndex(Number(e.target.value))}
            >
              <option value={-1}>Select</option>
              {dependents.map((d, i) =>
                d.name ? (
                  <option key={i} value={i}>
                    {d.name} ({d.relation || "no relation set"})
                  </option>
                ) : null
              )}
            </select>
            {dependents.length === 0 && (
              <p className="no-dependents-hint mt-1">Add a dependent above first, then pick them as nominee.</p>
            )}
          </div>
          <div className="col-md-3">
            <label className="form-label">Date of Birth</label>
            <input type="date" className="form-control" value={nominee?.dob || ""} disabled />
          </div>
          <div className="col-md-3">
            <label className="form-label">Relation</label>
            <input type="text" className="form-control" value={nominee?.relation || ""} disabled />
          </div>
        </div>
      </div>

      {isHr && (
        <div className="insurance-section">
          <div className="insurance-section-header">
            <h6>
              <BsFillHeartFill /> Health Plan
            </h6>
          </div>
          <div className="plan-row">
            <div>
              <label className="form-label">Plan</label>
              <select
                className="form-control"
                value={comboKey}
                onChange={(e) => handleComboSelect(e.target.value)}
              >
                <option value="">Select</option>
                {comboKey && !knownCombo && (
                  <option value={comboKey}>
                    {healthPlan.planName}
                    {opdPlan.planName ? ` + ${opdPlan.planName}` : ""} (current)
                  </option>
                )}
                {ONSURITY_PLAN_COMBOS.map((c) => (
                  <option key={c.key} value={c.key}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">Policy Type</label>
              <input type="text" className="form-control" value={healthPlan.policyType} disabled />
            </div>
          </div>
        </div>
      )}

      <div className="modal-btn-wrapper mt-4 mb-2">
        <div>
          <button className="btn cancel-btn buttonsize" onClick={handleEditInsurance}>
            {cancel}
          </button>
        </div>
        <div>
          <button className="btn add-submit-button buttonsize" onClick={handleSubmit}>
            {submit}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditInsuranceDetails;
